import type { WithID } from '@ps/models/lib/base';
import type { Profile } from '@ps/models/lib/profile';

export enum VideoType {
  Clips = 'clips',
  Review = 'review',
  ThankYou = 'thank-you',
  Bare = 'bare',
}

export type VideoInfoBase = {
  key: string;
  id: string;
  type: VideoType;
  author: WithID<Profile> | undefined;
  videoTitle?: string;
  videoDescription?: string;
  videoURL?: string;
  originalVideoURL?: string;
  videoCoverUrl?: string;
  gifURL?: string;
  thumbnailURL?: string;
  createdAt?:
    | { toDate: () => Date }
    | { seconds: number; nanoseconds: number }
    | Date
    | string
    | number;
  updatedAt?:
    | { toDate: () => Date }
    | { seconds: number; nanoseconds: number }
    | Date
    | string
    | number;
  listings?: VideoListing[];
  available?: boolean;
  visibility?: 'public' | 'private';
  extra?: Record<string, unknown>;
  taggedItemType?: 'products' | 'calendar' | 'external-links';
  taggedExternalLinks?: Array<{
    url: string;
    linkText: string;
    linkSubtext?: string;
    linkThumbnailUrl?: string;
    unfurled?: boolean;
  }>;
};

export type VideoListing = {
  id: string;
  /** Product name — PopshopRN sends `name`, clip-apis maps to `title` */
  name?: string;
  title?: string;
  defaultImage?: string;
  extraImages?: string[];
  price?: number;
  csProductUrl?: string;
};

export type PopClipVideoInfo = VideoInfoBase & {
  type: VideoType.Clips;
  clipId: string;
  cdnUrl: string;
  upvoteCount?: number;
  listings?: VideoListing[];
  accessLevel?: string;
  previewDuration?: string;
  showLockedVideoThumbnail?: boolean;
  muteForUnsubscribedUsers?: boolean;
  metaData?: {
    sellerId?: string;
    taggedItemType?: 'products' | 'calendar' | 'external-links';
    taggedExternalLinks?: Array<{
      url: string;
      linkText: string;
      linkSubtext?: string;
      linkThumbnailUrl?: string;
      unfurled?: boolean;
    }>;
    transcriptAttachment?: string;
  };
};

export type OrderReviewVideoInfo = VideoInfoBase & {
  type: VideoType.Review;
  messageId: string;
  orderId?: string;
  emoji?: string;
  upvoteCount?: number;
  from: WithID<Profile>;
  to: WithID<Profile>;
};

export type OrderThankYouVideoInfo = VideoInfoBase & {
  type: VideoType.ThankYou;
  messageId: string;
  sellerId: string;
  orderId?: string;
  from: WithID<Profile>;
  to: WithID<Profile>;
};

/**
 * A directly-playable public video URL that isn't backed by a clip record —
 * e.g. a creator's bio welcome video. `videoURL` (on the base) is the bare,
 * already-resolved public URL. Carries the same access-restriction fields as
 * `PopClipVideoInfo` so `ClipAccessGate` can paywall a restricted bare video
 * with no extra wiring; welcome videos leave them unset (always public).
 */
export type BareVideoInfo = VideoInfoBase & {
  type: VideoType.Bare;
  /** Creator whose surface owns this video (view + `sellerId` lookups). */
  sellerId: string;
  accessLevel?: string;
  previewDuration?: string;
  showLockedVideoThumbnail?: boolean;
  muteForUnsubscribedUsers?: boolean;
};

export type AnyVideo =
  | PopClipVideoInfo
  | OrderReviewVideoInfo
  | OrderThankYouVideoInfo
  | BareVideoInfo;

export type VideoInfo = {
  readonly sellerId: string | undefined;
  readonly primaryProfile: WithID<Profile> | undefined;
  readonly relatedProfile: WithID<Profile> | undefined;
  readonly videoId: string;
  readonly listings: VideoListing[];
  readonly defaultListing: VideoListing | undefined;
  readonly defaultListingImage: string | undefined;
};

// Comment types
export type BasicUserProfileInfo = {
  userId: string;
  fullName: string;
  avatar?: string;
  username?: string;
};

export type CommentMessageInfo = {
  id: string;
  userId: string;
  username?: string;
  text: string;
  name: string;
  isDeleted?: boolean;
  avatar?: string;
  status: 'uploading' | 'added' | 'failed';
  replyCount: number;
  createdAt: number;
  updatedAt: number;
  timestamp: number;
};

export type CommentMessage = CommentMessageInfo &
  ({ type: 'comment' } | { type: 'reply'; commentId: string });

export type CommentResponse = {
  user_id: string;
  comment_id: string;
  comment_text: string;
  user_meta: BasicUserProfileInfo;
  is_deleted: boolean;
  has_reply: boolean;
  timestamp: number;
  reply_count?: number;
  parent_comment_id: string | null;
  created_at: number;
  updated_at: number;
};
